import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import {
    FolderOpen, FileText, Loader2, AlertTriangle, Stethoscope, Printer,
    Copy, Check, Mail,
} from "lucide-react";

/* Public read-only view of a GUIDE-screener bundle, at /share/guide-screeners/:token.
 * No auth — the token itself is the credential.
 *
 * Layout:
 *  1. Patient/caregiver header
 *  2. "For the clinical team" auto-populated scores panel (when scores exist)
 *  3. Full packet — each form with answered items
 *  4. Toolbar: Print / Save as PDF · Copy link · Email
 */
export default function GuideScreenersShare() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/documents/guide/share/${token}`);
                if (active) setData(r.data);
            } catch (e) {
                if (active) setError(e?.response?.status === 410 ? "This link has expired." : "This link is no longer valid.");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [token]);

    return (
        <div className="min-h-screen bg-cream">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 space-y-8">
                <header>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-stage_late/40 flex items-center justify-center">
                            <FolderOpen size={20} strokeWidth={1.6} className="text-stage_late-dark" />
                        </div>
                        <p className="text-sm uppercase tracking-wider font-semibold text-[#6E7A74]">
                            GUIDE Pre-visit Enrollment Screeners · shared with you
                        </p>
                    </div>
                    <h1 className="mt-3 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-[#12776F]">
                        Pre-visit packet
                    </h1>
                    {data?.patient_name && (
                        <p className="mt-2 text-lg text-[#4C5551]">For <strong className="text-[#1F2624]">{data.patient_name}</strong>{data.caregiver_name ? ` · shared by ${data.caregiver_name}` : ""}.</p>
                    )}
                </header>

                {loading && (
                    <div className="flex items-center gap-2 text-[#6E7A74]"><Loader2 className="animate-spin" size={18} /> Loading…</div>
                )}

                {error && (
                    <div className="ml-card p-6 bg-white text-center" data-testid="share-error">
                        <p className="text-[#1F2624] font-medium">{error}</p>
                        <p className="text-sm text-[#6E7A74] mt-1">Ask the caregiver to send you a fresh link.</p>
                    </div>
                )}

                {data && (
                    <div data-clarity-mask="true">
                        <ShareToolbar patientName={data.patient_name} caregiverName={data.caregiver_name} />
                        <ClinicalScoresSection rows={data.score_rows || []} flags={data.key_flags || []} />
                        <FormPacket forms={data.forms || []} responses={data.responses || {}} />
                    </div>
                )}
            </div>
        </div>
    );
}

function ShareToolbar({ patientName, caregiverName }) {
    const [copied, setCopied] = useState(false);
    const url = typeof window !== "undefined" ? window.location.href : "";
    const subject = patientName ? `GUIDE pre-visit screeners for ${patientName}` : "GUIDE pre-visit screeners";
    const body = `${caregiverName ? caregiverName + " " : ""}shared a GUIDE pre-visit packet:\n\n${url}\n`;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch { /* noop */ }
    };
    const sendEmail = () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };
    return (
        <div className="flex flex-wrap gap-2 print:hidden" data-testid="share-toolbar">
            <button type="button" onClick={() => window.print()} data-testid="share-print"
                    className="px-4 py-2 rounded-2xl text-sm bg-teal-dark text-white inline-flex items-center gap-1.5 hover:bg-teal">
                <Printer size={14} /> Print / Save as PDF
            </button>
            <button type="button" onClick={copyLink} data-testid="share-copy-link"
                    className="px-4 py-2 rounded-2xl text-sm bg-white border border-cream-dark text-[#1F2624] inline-flex items-center gap-1.5 hover:bg-cream-dark/30">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
            </button>
            <button type="button" onClick={sendEmail} data-testid="share-email"
                    className="px-4 py-2 rounded-2xl text-sm bg-white border border-cream-dark text-[#1F2624] inline-flex items-center gap-1.5 hover:bg-cream-dark/30">
                <Mail size={14} /> Email
            </button>
        </div>
    );
}

function ClinicalScoresSection({ rows, flags }) {
    const scored = rows.filter(r => r.score !== null && r.score !== undefined);
    if (scored.length === 0) return null;
    return (
        <section className="ml-card p-0 bg-sage-light/60 border border-sage-soft overflow-hidden" data-testid="share-clinical-scores">
            <div className="px-5 md:px-6 py-4 border-b border-sage-soft bg-sage-light/80 flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-sage text-white flex items-center justify-center shrink-0">
                    <Stethoscope size={18} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-xl md:text-2xl font-heading font-medium text-[#12776F]">Clinical scores</h2>
                    <p className="text-sm text-[#4C5551] mt-0.5">
                        Auto-computed from the caregiver&apos;s answers. Raw responses live in the full packet below.
                    </p>
                </div>
            </div>
            <div className="px-5 md:px-6 py-5">
                {flags.length > 0 && (
                    <div className="mb-4 rounded-2xl bg-yellow-light border border-yellow p-4" data-testid="share-key-flags">
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle size={16} className="text-yellow-dark" />
                            <p className="text-sm uppercase tracking-wider font-semibold text-yellow-dark">
                                Things to mention ({flags.length})
                            </p>
                        </div>
                        <ul className="space-y-1 text-sm text-[#1F2624]">
                            {flags.map((f, i) => (
                                <li key={i} data-testid={`share-flag-${i}`}>• {f}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="overflow-x-auto rounded-2xl bg-white border border-sage-soft">
                    <table className="w-full text-sm" data-testid="share-score-table">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                <th className="py-2 px-3 font-semibold">Screener</th>
                                <th className="py-2 px-3 font-semibold">Score</th>
                                <th className="py-2 px-3 font-semibold">Band</th>
                            </tr>
                        </thead>
                        <tbody>
                            {scored.map(r => (
                                <tr key={r.id} className={`border-b border-cream-light ${r.flag ? "bg-yellow-light/40" : ""}`} data-testid={`share-score-row-${r.id}`}>
                                    <td className="py-2.5 px-3 text-[#1F2624]">{r.title}</td>
                                    <td className="py-2.5 px-3 font-mono text-[#1F2624]">
                                        {formatScore(r.score)}
                                        {r.max_score !== null && r.max_score !== undefined && typeof r.max_score === "number" && (
                                            <span className="text-[#6E7A74]"> / {r.max_score}</span>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-3 text-[#4C5551]">{r.band || <span className="text-[#6E7A74]">—</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

function FormPacket({ forms, responses }) {
    return (
        <article className="ml-card p-5 md:p-7 bg-white" data-testid="share-forms">
            <p className="text-sm text-[#4C5551] mb-4">
                The caregiver has selected the following {forms.length} screener{forms.length === 1 ? "" : "s"} from the CMS GUIDE pre-visit packet:
            </p>
            <ul className="space-y-4">
                {forms.map(f => {
                    const resp = responses[f.id] || { answers: {}, free_text: {} };
                    const answers = resp.answers || {};
                    const freeText = resp.free_text || {};
                    return (
                        <li key={f.id} data-testid={`shared-form-${f.id}`} className="p-4 rounded-2xl bg-cream-light border border-cream-dark">
                            <div className="flex items-start gap-3">
                                <FileText size={18} strokeWidth={1.6} className="text-stage_late-dark mt-0.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-[#1F2624]">{f.title}</p>
                                    <p className="text-sm text-[#4C5551] mt-0.5">{f.purpose}</p>
                                    <p className="text-xs text-[#6E7A74] mt-1">
                                        Section {f.section} · GUIDE component {f.guide_map} · {f.audience}
                                        {f.max_score ? ` · max ${f.max_score}` : ""}
                                    </p>
                                </div>
                            </div>
                            {(f.items || []).length > 0 && (
                                <div className="mt-3 space-y-1.5">
                                    {(f.items || []).map(item => {
                                        const val = answers[item.id];
                                        const labelForVal = labelFor(item.scale, val);
                                        return (
                                            <div key={item.id} className="flex items-baseline gap-3 text-sm" data-testid={`shared-answer-${f.id}-${item.id}`}>
                                                <span className="text-[#4C5551] flex-1">{item.label}</span>
                                                <span className={`font-medium font-mono shrink-0 ${val === undefined || val === "" ? "text-[#6E7A74]" : "text-[#1F2624]"}`}>
                                                    {val === undefined || val === "" ? "—" : labelForVal}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {(f.free_text_fields || []).length > 0 && (
                                <div className="mt-3 space-y-1.5">
                                    {(f.free_text_fields || []).map(ft => (
                                        <div key={ft.id} className="text-sm" data-testid={`shared-freetext-${f.id}-${ft.id}`}>
                                            <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">{ft.label}</p>
                                            <p className={`mt-0.5 whitespace-pre-wrap ${freeText[ft.id] ? "text-[#1F2624]" : "text-[#6E7A74]"}`}>
                                                {freeText[ft.id] || "—"}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
            <p className="mt-5 text-xs text-[#6E7A74]">
                Read-only view shared by the caregiver via Memory Lane Care Navigator. This is not a medical record.
            </p>
        </article>
    );
}

function labelFor(scale, value) {
    if (!Array.isArray(scale)) return String(value);
    const found = scale.find(opt => String(opt.value) === String(value));
    return found ? found.label : String(value);
}

function formatScore(score) {
    if (score === null || score === undefined || score === "") return <span className="text-[#6E7A74]">—</span>;
    if (typeof score === "number") return Number.isInteger(score) ? score : score.toFixed(1);
    return String(score);
}
